<template>
  <basic-dialog :show="show" @close="$emit('close')" max-width="800">
    <template v-slot:title>{{ editorTitle }}</template>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="name"
          label="Task Name"
          :outlined="outlinedInputs"
          hide-details
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-select
          :items="statusesList"
          v-model="taskStatus"
          item-text="text"
          item-value="value"
          label="Status"
          :outlined="outlinedInputs"
          hide-details
        ></v-select>
      </v-col>
      <v-col>
        <v-select
          label="Task Priority"
          v-model="priority"
          :items="prioritiesList"
          item-text="name"
          item-value="name"
          :outlined="outlinedInputs"
          hide-details
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6">
        <date-picker
          customLabel="Due Date"
          @onDateSelected="onStartDateSelected"
          :gtDateString="estimatedStart"
          :outlined="outlinedInputs"
        ></date-picker>
      </v-col>
      <v-col cols="12" sm="6">
        <booked-by-list-select
          label="Assigned To"
          :agent-id="agentId"
          v-model="userId"
          :outlined="outlinedInputs"
        ></booked-by-list-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6">
        <v-select
          v-if="inItinerary"
          :items="availableProjectTypes"
          v-model="taskType"
          @change="onProjectTypeSelect"
          item-text="text"
          item-value="value"
          label="Type"
          :outlined="outlinedInputs"
          hide-details
        ></v-select>
        <v-text-field
          v-if="!inItinerary"
          :value="taskTypeLabel"
          label="Type"
          disabled
          :outlined="outlinedInputs"
        >
        </v-text-field>
      </v-col>
      <v-col cols="12" sm="6">
        <v-select
          v-if="inItinerary"
          :items="availableProjects"
          v-model="projectId"
          @change="onProjectSelect"
          item-text="text"
          item-value="value"
          :label="taskTypeLabel"
          :outlined="outlinedInputs"
          hide-details
        ></v-select>
        <v-text-field
          v-if="!inItinerary"
          v-model="project"
          :label="taskTypeLabel"
          disabled
          :outlined="outlinedInputs"
        >
        </v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-textarea
          height="100"
          v-model="notes"
          label="Notes"
          :outlined="outlinedInputs"
          hide-details
        ></v-textarea>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="12">
        <itinerary-task-actions-list
          @save="onActionSave"
          @delete="onActionDelete"
          :items="taskActions"
        >
        </itinerary-task-actions-list>
      </v-col>
    </v-row>
    <email-dialog
      :show="showEmailDialog"
      v-model="email"
      @close="showEmailDialog = false"
      @send="onSendMessage"
    ></email-dialog>
    <template v-slot:actions>
      <v-btn v-if="showContactBtn" @click="onContact"
        >Contact {{ taskTypeLabel }}</v-btn
      >
      <v-btn color="primary" @click="onSave" :disabled="!isValid">Save</v-btn>
    </template>
    <v-row v-if="debug"
      ><v-col>
        <code>{{ userId }}</code>
      </v-col></v-row
    >
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../shared/BasicDialog";
import DatePicker from "../../shared/DatePicker";
import BookedByListSelect from "../../shared/BookedByListSelect";
import ItineraryTaskActionsList from "./ItineraryTaskActionsList.vue";
import EmailDialog from "../../shared/EmailDialog.vue";
import { SEND_CLIENT_MESSAGE } from "../../../../constants/graphql";
import gql from "graphql-tag";
import { removeGQLTypename } from "../../../../helpers";
const STATUSES_LIST = gql`
  query allItineraryTaskStatuses {
    itineraryTaskStatuses {
      edges {
        node {
          id
          name
          isDefault
        }
      }
    }
  }
`;
const PRIORITIES_LIST = gql`
  query allItineraryTaskPriorities {
    itineraryTaskPriorities {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
export default {
  name: "edit-itinerary-task-dialog",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => null,
    },
    itinerary: {
      type: Object,
      default: () => null,
    },
  },

  components: {
    BasicDialog,
    DatePicker,
    BookedByListSelect,
    ItineraryTaskActionsList,
    EmailDialog,
  },

  data() {
    return {
      availableStatuses: [
        { text: "New", value: "new" },
        { text: "In Progress", value: "in progress" },
        { text: "Resolved", value: "resolved" },
      ],
      availableProjectTypes: [
        { text: "Itinerary", value: "itinerary" },
        { text: "Client", value: "client" },
        { text: "Agent", value: "agent" },
        { text: "Supplier", value: "supplier" },
      ],
      availableProjects: [],
      debug: false,
      agentId: this.$store.getters.agentId,
      itineraryId: this.value ? this.value.itineraryId : null,
      taskStatus: this.value ? this.value.taskStatus : "new",
      taskType: this.value ? this.value.taskType : "itinerary",
      project: this.value ? this.value.project : "",
      projectId: this.value ? this.value.projectId : null,
      name: this.value ? this.value.name : "",
      notes: this.value ? this.value.notes : "",
      estimatedStart: this.value
        ? this.value.estimatedStart
        : new Date().toISOString(),
      userId: this.value ? this.value.userId : null,
      priority: this.value ? this.value.priority : "",
      taskActions:
        this.value && this.value.taskActions
          ? this.value.taskActions.edges.map((e) => e.node)
          : [],
      showEmailDialog: false,
      email: null,
    };
  },

  computed: {
    showContactBtn() {
      return this.taskType != "itinerary";
    },
    taskTypeLabel() {
      const t = this.taskType || "itinerary";
      return t.charAt(0).toUpperCase() + t.slice(1);
    },
    inItinerary() {
      return !!this.itinerary;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    editorTitle() {
      return `${this.value && this.value.id ? "Edit" : "Add"} - ${this.name}`;
    },
    isValid() {
      return this.name != "" && this.userId && this.projectId;
    },
    statusesList() {
      return this.itineraryTaskStatuses && this.itineraryTaskStatuses.edges
        ? [
            ...this.availableStatuses,
            ...this.itineraryTaskStatuses.edges
              .map(removeGQLTypename)
              .map((s) => ({ text: s.name, value: s.name })),
          ]
        : [...this.availableStatuses];
    },
    prioritiesList() {
      return this.itineraryTaskPriorities && this.itineraryTaskPriorities.edges
        ? this.itineraryTaskPriorities.edges.map(removeGQLTypename)
        : [];
    },
  },

  watch: {
    value(newVal) {
      //defaults
      this.name = newVal.name;
      this.taskStatus = "new";
      this.taskType = "itinerary";
      this.estimatedStart = new Date().toISOString();
      this.taskActions = [];
      this.project = undefined;
      this.projectId = undefined;
      this.itineraryId = undefined;
      this.id = undefined;
      this.userId = undefined;
      this.notes = "";
      this.priority = newVal.priority;
      //updated values
      Object.assign(this, newVal);
      if (newVal.taskActions && newVal.taskActions.hasOwnProperty("edges")) {
        this.taskActions = newVal.taskActions.edges.map(removeGQLTypename);
      }
      this.onProjectTypeSelect(this.taskType);
    },
  },

  methods: {
    onContact() {
      let emailTo = "";
      switch (this.taskType) {
        case "client":
          const selectedClient = this.itinerary.participants.edges.find(
            (item) => item.node.client.id == this.projectId
          );
          if (selectedClient) {
            emailTo = selectedClient.node.client.email;
          }
          break;
        case "agent":
          emailTo = this.itinerary.agent.email;
          break;
        case "supplier":
          const selectedSupplier = this.itinerary.items.edges.find(
            (item) => item.node.supplier.id == this.projectId
          );
          if (selectedSupplier) {
            emailTo = selectedSupplier.node.supplier.email;
          }
          break;
      }
      const mail = {
        emailTo,
        ccTo: this.itinerary.createdBy.email,
        subject: this.name,
        message: this.notes,
      };
      this.email = { ...mail };
      this.showEmailDialog = true;
    },
    onSendMessage(payload) {
      this.$apollo
        .mutate({
          mutation: SEND_CLIENT_MESSAGE,
          variables: { input: { ...payload, itineraryId: this.itinerary.id } },
        })
        .then((res) => {
          if (res.data.contactClient.success) {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Message Sent",
            });
            this.showEmailDialog = false;
          } else {
            //res.data.ContactClient.success
            this.$store.dispatch("showAppSnackbar", {
              msg: res.data.contactClient.error,
              color: "error",
            });
          }
        })
        .catch((err) => {
          //console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
          console.log(err);
        });
    },
    onProjectTypeSelect(projectType) {
      //console.log("on project change", this.itinerary.participants);
      if (this.itinerary && projectType) {
        const data = {
          itinerary: [
            {
              text: this.itinerary.reference.split("/").pop(),
              value: this.itinerary.id,
            },
          ],
          agent: [
            {
              text: this.itinerary.agent.name,
              value: this.itinerary.agent.id,
            },
          ],
          client: this.itinerary.participants.edges
            .filter((c) => c.node.client)
            .map((c) => ({
              text: `${c.node.client.name} ${c.node.client.surname}`,
              value: c.node.client.id,
            })),
          supplier: this.itinerary.items.edges
            .filter(({ node }) => !!node.supplier)
            .map(({ node }) => {
              return { text: node.supplierName, value: node.supplier.id };
            }),
        };
        this.availableProjects = data[projectType];
      }
      if (this.availableProjects.length == 1) {
        this.projectId = this.availableProjects[0].value;
      }
    },
    onActionSave(action) {
      const idx = action.id
        ? this.taskActions.findIndex((a) => a.id == action.id)
        : this.taskActions.findIndex((a) => a._uid == action._uid);
      if (idx > -1) {
        // this.taskActions[idx] = action;
        // this.taskActions = this.taskActions.map((a) => a); //not refreshing otherwise
        Object.assign(this.taskActions[idx], { ...action });
      } else {
        this.taskActions.push(action);
      }
    },
    onActionDelete(action) {
      if (confirm("Are you sure you want to delete this action?")) {
        const idx = action.id
          ? this.taskActions.findIndex((a) => a.id == action.id)
          : this.taskActions.findIndex((a) => a._uid == action._uid);
        this.taskActions.splice(idx, 1);
      }
    },
    onProjectSelect(value) {
      this.projectId = value;
    },

    onStartDateSelected(value) {
      this.estimatedStart = value;
    },
    onSave() {
      const input = {
        id: this.value && this.value.id ? this.value.id : undefined,
        name: this.name,
        itineraryId: this.itinerary
          ? this.itinerary.id
          : this.value
          ? this.value.itineraryId
          : undefined,
        notes: this.notes,
        estimatedStart: this.estimatedStart,
        userId: this.userId,
        taskStatus: this.taskStatus,
        projectId: this.projectId,
        taskType: this.taskType,
        priority: this.priority,
        taskActions: this.taskActions.map(
          ({ _uid, __typename, updatedAt, userName, ...rest }) => rest
        ), //remove _uid && __typename helper field
      };
      /*
      if (this.inItinerary) {
        input = Object.assign(input,{
          projectId: this.projectId,
          taskType: this.taskType
        });
      }
      */
      this.$emit("input", input);
    },
  },
  apollo: {
    itineraryTaskStatuses: {
      query: STATUSES_LIST,
      fetchPolicy: "network-only",
    },
    itineraryTaskPriorities: {
      query: PRIORITIES_LIST,
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/plugins/task-tracker/EditItineraryTaskDialog.vue